import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Avatar,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import api from '../../services/api';
import sectionAvatarService from '../../services/sectionAvatarService';
import sectionColorService from '../../services/sectionColorService';
import EditIcon from '@mui/icons-material/Edit';
import PermissionGate from '../common/PermissionGate';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

// Import all avatar images from foundation component
import face1 from '../../assets/avatars/Face-1.png';
import face2 from '../../assets/avatars/Face-2.png';
import face from '../../assets/avatars/Face.png';
import faceCo1 from '../../assets/avatars/face_co (1) 4.png';
import faceCo12 from '../../assets/avatars/face_co (12) 4.png';
import faceCo13 from '../../assets/avatars/face_co (13) 4.png';
import faceCo14 from '../../assets/avatars/face_co (14) 4.png';
import faceCo15 from '../../assets/avatars/face_co (15) 4.png';
import faceCo16 from '../../assets/avatars/face_co (16) 4.png';
import faceCo17 from '../../assets/avatars/face_co (17) 4.png';
import faceCo18 from '../../assets/avatars/face_co (18) 4.png';
import faceCo19 from '../../assets/avatars/face_co (19) 4.png';
import faceCo2 from '../../assets/avatars/face_co (2) 4.png';
import faceCo20 from '../../assets/avatars/face_co (20) 4.png';
import faceCo21 from '../../assets/avatars/face_co (21) 4.png';
import faceCo23 from '../../assets/avatars/face_co (23) 4.png';
import faceCo24 from '../../assets/avatars/face_co (24) 4.png';
import faceCo25 from '../../assets/avatars/face_co (25) 4.png';
import faceCo26 from '../../assets/avatars/face_co (26) 4.png';
import faceCo27 from '../../assets/avatars/face_co (27) 5.png';
import faceCo28 from '../../assets/avatars/face_co (28) 4.png';
import faceCo29 from '../../assets/avatars/face_co (29) 4.png';
import faceCo3 from '../../assets/avatars/face_co (3) 4.png';
import faceCo30 from '../../assets/avatars/face_co (30) 5.png';
import faceCo31 from '../../assets/avatars/face_co (31) 4.png';
import faceCo32 from '../../assets/avatars/face_co (32) 4.png';
import faceCo33 from '../../assets/avatars/face_co (33) 4.png';
import faceCo34 from '../../assets/avatars/face_co (34) 4.png';
import faceCo35 from '../../assets/avatars/face_co (35) 4.png';
import faceCo36 from '../../assets/avatars/face_co (36) 4.png';
import faceCo37 from '../../assets/avatars/face_co (37) 4.png';
import faceCo38 from '../../assets/avatars/face_co (38) 5.png';
import faceCo39 from '../../assets/avatars/face_co (39) 4.png';
import faceCo4 from '../../assets/avatars/face_co (4) 4.png';
import faceCo40 from '../../assets/avatars/face_co (40) 4.png';
import faceCo41 from '../../assets/avatars/face_co (41) 4.png';
import faceCo42 from '../../assets/avatars/face_co (42) 4.png';
import faceCo43 from '../../assets/avatars/face_co (43) 4.png';
import faceCo44 from '../../assets/avatars/face_co (44) 4.png';
import faceCo45 from '../../assets/avatars/face_co (45) 4.png';
import faceCo46 from '../../assets/avatars/face_co (46) 4.png';
import faceCo47 from '../../assets/avatars/face_co (47) 4.png';
import faceCo48 from '../../assets/avatars/face_co (48) 4.png';
import faceCo49 from '../../assets/avatars/face_co (49) 4.png';
import faceCo5 from '../../assets/avatars/face_co (5) 4.png';
import faceCo51 from '../../assets/avatars/face_co (51) 4.png';
import faceCo6 from '../../assets/avatars/face_co (6) 4.png';
import faceCo7 from '../../assets/avatars/face_co (7) 4.png';
import faceCo8 from '../../assets/avatars/face_co (8) 4.png';
import faceCo from '../../assets/avatars/face_co 4.png';

/**
 * Section Orchestrator Selector Component
 * Shows an orchestrator label, avatar image, and name for each section
 * Allows for editing when projectId is provided
 *
 * @param {Object} props - Component props
 * @param {string} props.sectionId - ID of the section
 * @param {string} props.sectionName - Name of the section
 * @param {string} props.projectId - ID of the current project
 * @param {boolean} props.allowEdit - Whether to allow editing the orchestrator (default: true)
 * @returns {React.ReactElement} SectionOrchestratorSelector component
 */
const SectionOrchestratorSelector = ({ sectionId, sectionName, projectId, allowEdit = true }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [avatarData, setAvatarData] = useState({
    url: null,
    name: null,
    sectionId: sectionId
  });
  
  // Avatar selection dialog state
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [customAvatarName, setCustomAvatarName] = useState('');
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [nameError, setNameError] = useState('');
  // State for highlighting avatar after save
  const [justSaved, setJustSaved] = useState(false);
  
  // Available avatars for selection
  const [availableAvatars, setAvailableAvatars] = useState([]);
  const [loadingAvatars, setLoadingAvatars] = useState(false);

  // No default avatar names - always fetch from API
  const getDefaultAvatarName = (sectionName) => {
    // We won't use default names, always wait for data from the API
    return '';
  };

  // Fetch avatar data for the section
  useEffect(() => {
    const fetchSectionAvatar = async () => {
      try {
        setLoading(true);

        // Log component initialization with projectId and sectionId for debugging
        console.log(`SectionOrchestratorSelector initializing for section=${sectionId}, project=${projectId}`);
        console.log(`Current avatarData:`, avatarData);

        // List all avatar storage for debugging
        sectionAvatarService.listAllAvatarStorage();

        // Handle no or invalid projectId case early
        if (!projectId) {
          console.log('No projectId provided, cannot fetch project-specific orchestrator');
          setAvatarData({
            url: null,
            name: null,
            sectionId: sectionId
          });
          setLoading(false);
          return;
        }

        // Always fetch fresh data from the API for all projects
        console.log(`Fetching fresh data for project=${projectId}`);

        // If no section ID, return empty data with default name
        if (!sectionId) {
          const defaultName = getDefaultAvatarName(sectionName);
          setAvatarData({
            url: null,
            name: null,
            sectionId: sectionId
          });
          // Set custom avatar name for UI
          setCustomAvatarName(defaultName);
          setLoading(false);
          return;
        }

        // Don't use session storage, always fetch fresh from API
        console.log(`Always fetching fresh avatar data for section=${sectionId}, project=${projectId}`);

        // If we have a project ID, only check for orchestrators linked to this project
        if (projectId) {
          // Always fetch from API for all projects
          console.log(`Fetching orchestrator data from API for section=${sectionId}, project=${projectId}`);

          try {
            // Use the avatar service instead of direct API calls
            const data = await sectionAvatarService.getSectionAvatar(sectionId, projectId);

            if (data.success && (data.avatarUrl || data.avatarName)) {
              // Successfully got project-specific avatar data
              const newAvatarData = {
                url: data.avatarUrl || null,
                name: data.avatarName || null,
                sectionId: sectionId
              };

              // Don't cache results, always fetch fresh from API
              setAvatarData(newAvatarData);

              if (data.avatarName) {
                setCustomAvatarName(data.avatarName);
              }

              // Force a reload of the image if it's a URL to avoid browser cache issues
              if (data.avatarUrl) {
                const forceReloadUrl = data.avatarUrl.includes('?')
                  ? `${data.avatarUrl}&_nocache=${Date.now()}`
                  : `${data.avatarUrl}?_nocache=${Date.now()}`;
                console.log('Using cache-busted avatar URL:', forceReloadUrl);
                setAvatarData(prev => ({...prev, url: forceReloadUrl}));
              }

              setLoading(false);
              return;
            }

            // If no project-specific avatar exists, set to empty to show the plus icon
            const emptyData = {
              url: null,
              name: null,
              sectionId: sectionId
            };
            // Don't cache in sessionStorage
            setAvatarData(emptyData);
          } catch (err) {
            console.error('Error fetching project-specific avatar:', err);
            // Set to empty if there's an error
            const emptyData = {
              url: null,
              name: null,
              sectionId: sectionId
            };
            // Don't cache in sessionStorage
            setAvatarData(emptyData);
          }
        } else {
          // No project ID provided - we don't want to show any orchestrator
          // This is a display-only view with no project context
          const emptyData = {
            url: null,
            name: null,
            sectionId: sectionId
          };
          // Don't cache in sessionStorage
          setAvatarData(emptyData);
        }

        setError(null);
      } catch (err) {
        console.error('Error in avatar fetch flow:', err);
        setError('Failed to load orchestrator data');

        // Always set to empty on error
        setAvatarData({
          url: null,
          name: null,
          sectionId: sectionId
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSectionAvatar();
  }, [sectionId, sectionName, projectId]);

  // Handle image loading error
  const handleImageError = () => {
    setAvatarData(prev => ({
      ...prev,
      url: null
    }));
  };
  
  // Prepare all avatar images
  const fetchAvatars = () => {
    try {
      setLoadingAvatars(true);
      
      // Create array of all avatar objects with imported files
      const avatarList = [
        { id: 1, name: 'Avatar 1', photoUrl: face1, type: 'robot' },
        { id: 2, name: 'Avatar 2', photoUrl: face2, type: 'robot' },
        { id: 3, name: 'Avatar 3', photoUrl: face, type: 'robot' },
        { id: 4, name: 'Avatar 4', photoUrl: faceCo1, type: 'robot' },
        { id: 5, name: 'Avatar 5', photoUrl: faceCo12, type: 'robot' },
        { id: 6, name: 'Avatar 6', photoUrl: faceCo13, type: 'robot' },
        { id: 7, name: 'Avatar 7', photoUrl: faceCo14, type: 'robot' },
        { id: 8, name: 'Avatar 8', photoUrl: faceCo15, type: 'robot' },
        { id: 9, name: 'Avatar 9', photoUrl: faceCo16, type: 'robot' },
        { id: 10, name: 'Avatar 10', photoUrl: faceCo17, type: 'robot' },
        { id: 11, name: 'Avatar 11', photoUrl: faceCo18, type: 'robot' },
        { id: 12, name: 'Avatar 12', photoUrl: faceCo19, type: 'robot' },
        { id: 13, name: 'Avatar 13', photoUrl: faceCo2, type: 'robot' },
        { id: 14, name: 'Avatar 14', photoUrl: faceCo20, type: 'robot' },
        { id: 15, name: 'Avatar 15', photoUrl: faceCo21, type: 'robot' },
        { id: 16, name: 'Avatar 16', photoUrl: faceCo23, type: 'robot' },
        { id: 17, name: 'Avatar 17', photoUrl: faceCo24, type: 'robot' },
        { id: 18, name: 'Avatar 18', photoUrl: faceCo25, type: 'robot' },
        { id: 19, name: 'Avatar 19', photoUrl: faceCo26, type: 'robot' },
        { id: 20, name: 'Avatar 20', photoUrl: faceCo27, type: 'robot' },
        { id: 21, name: 'Avatar 21', photoUrl: faceCo28, type: 'robot' },
        { id: 22, name: 'Avatar 22', photoUrl: faceCo29, type: 'robot' },
        { id: 23, name: 'Avatar 23', photoUrl: faceCo3, type: 'robot' },
        { id: 24, name: 'Avatar 24', photoUrl: faceCo30, type: 'robot' },
        { id: 25, name: 'Avatar 25', photoUrl: faceCo31, type: 'robot' },
        { id: 26, name: 'Avatar 26', photoUrl: faceCo32, type: 'robot' },
        { id: 27, name: 'Avatar 27', photoUrl: faceCo33, type: 'robot' },
        { id: 28, name: 'Avatar 28', photoUrl: faceCo34, type: 'robot' },
        { id: 29, name: 'Avatar 29', photoUrl: faceCo35, type: 'robot' },
        { id: 30, name: 'Avatar 30', photoUrl: faceCo36, type: 'robot' },
        { id: 31, name: 'Avatar 31', photoUrl: faceCo37, type: 'robot' },
        { id: 32, name: 'Avatar 32', photoUrl: faceCo38, type: 'robot' },
        { id: 33, name: 'Avatar 33', photoUrl: faceCo39, type: 'robot' },
        { id: 34, name: 'Avatar 34', photoUrl: faceCo4, type: 'robot' },
        { id: 35, name: 'Avatar 35', photoUrl: faceCo40, type: 'robot' },
        { id: 36, name: 'Avatar 36', photoUrl: faceCo41, type: 'robot' },
        { id: 37, name: 'Avatar 37', photoUrl: faceCo42, type: 'robot' },
        { id: 38, name: 'Avatar 38', photoUrl: faceCo43, type: 'robot' },
        { id: 39, name: 'Avatar 39', photoUrl: faceCo44, type: 'robot' },
        { id: 40, name: 'Avatar 40', photoUrl: faceCo45, type: 'robot' },
        { id: 41, name: 'Avatar 41', photoUrl: faceCo46, type: 'robot' },
        { id: 42, name: 'Avatar 42', photoUrl: faceCo47, type: 'robot' },
        { id: 43, name: 'Avatar 43', photoUrl: faceCo48, type: 'robot' },
        { id: 44, name: 'Avatar 44', photoUrl: faceCo49, type: 'robot' },
        { id: 45, name: 'Avatar 45', photoUrl: faceCo5, type: 'robot' },
        { id: 46, name: 'Avatar 46', photoUrl: faceCo51, type: 'robot' },
        { id: 47, name: 'Avatar 47', photoUrl: faceCo6, type: 'robot' },
        { id: 48, name: 'Avatar 48', photoUrl: faceCo7, type: 'robot' },
        { id: 49, name: 'Avatar 49', photoUrl: faceCo8, type: 'robot' },
        { id: 50, name: 'Avatar 50', photoUrl: faceCo, type: 'robot' },
      ];
      
      setAvailableAvatars(avatarList);
      setError(null);
    } catch (err) {
      console.error('Error preparing avatar images:', err);
      setError('Failed to load avatar images');
    } finally {
      setLoadingAvatars(false);
    }
  };
  
  // Handle opening avatar dialog
  const handleOpenAvatarDialog = () => {
    // Debug: List all avatar storage on click
    sectionAvatarService.listAllAvatarStorage();

    console.log(`Opening avatar dialog for section=${sectionId}, project=${projectId}, allowEdit=${allowEdit}`);

    // Always require a proper projectId
    if (!projectId) {
      console.log('No projectId provided. Cannot fetch orchestrator without project context.');
    }

    // We'll allow opening the dialog even without projectId, and handle validation later
    // This ensures users can at least see the dialog
    fetchAvatars();
    setSelectedAvatar(null);

    // Don't suggest default names, only use what comes from API
    if (avatarData.name) {
      setCustomAvatarName(avatarData.name);
    } else {
      setCustomAvatarName('');
    }

    setNameError('');
    setAvatarDialogOpen(true);
  };
  
  // Handle closing avatar dialog
  const handleCloseAvatarDialog = () => {
    setAvatarDialogOpen(false);
  };
  
  // Handle avatar selection in dialog
  const handleSelectAvatar = (avatar) => {
    if (!avatar) return;
    console.log('Selected avatar:', avatar);
    setSelectedAvatar(avatar);

    // Pre-populate the name field if it's empty and the avatar has a name
    if (!customAvatarName) {
      if (avatar.name) {
        setCustomAvatarName(avatar.name);
      }
    }
  };
  
  // Handle saving selected avatar
  const handleSaveAvatar = async () => {
    // Validation
    if (!customAvatarName.trim()) {
      setNameError('Please enter a name for the orchestrator');
      return;
    }

    // Check if we have a project ID, but don't block the save attempt
    // Instead show a warning that will be displayed in the helper text
    if (!projectId) {
      console.warn('Attempting to save orchestrator without project context');
      setNameError('Warning: Project context not found, changes may not persist');
      // Continue with save attempt anyway
    } else {
      // Reset errors if we have a project ID
      setNameError('');
    }

    try {
      setSavingAvatar(true);

      // Debug log cache before save
      console.log(`SectionOrchestratorSelector: Before saving, current avatars in storage:`);
      sectionAvatarService.listAllAvatarStorage();

      // Only use the projectId from props, never from sessionStorage
      const effectiveProjectId = projectId;

      // Create data for avatar selection
      const photoUrl = selectedAvatar ? selectedAvatar.photoUrl : avatarData.url || null;
      console.log(`Using photoUrl for save: ${photoUrl}, selected avatar:`, selectedAvatar);

      const newAvatarData = {
        sectionId,
        avatarName: customAvatarName.trim(),
        photoUrl: photoUrl,
        type: selectedAvatar ? selectedAvatar.type || 'robot' : 'robot',
        projectId: effectiveProjectId // Use the effective project ID
      };

      // Send to backend with explicit project context using the avatar service
      console.log(`SectionOrchestratorSelector: Saving avatar for section=${sectionId}, project=${effectiveProjectId}`, newAvatarData);
      const response = await sectionAvatarService.saveSectionAvatar(sectionId, effectiveProjectId, newAvatarData);

      // Update UI
      if (response && response.success) {
        // Get the correct avatar URL from the response, with multiple fallbacks
        let avatarUrl = null;
        if (response.avatarUrl) {
          avatarUrl = response.avatarUrl;
          console.log('Using avatarUrl from response:', avatarUrl);
        } else if (selectedAvatar?.photoUrl) {
          avatarUrl = selectedAvatar.photoUrl;
          console.log('Using photoUrl from selectedAvatar:', avatarUrl);
        } else if (avatarData.url) {
          avatarUrl = avatarData.url;
          console.log('Using url from existing avatarData:', avatarUrl);
        }

        const updatedData = {
          url: avatarUrl,
          name: response.avatarName || customAvatarName,
          sectionId: sectionId
        };

        console.log(`SectionOrchestratorSelector: Avatar saved successfully:`, updatedData);

        // Update state with a force flag to trigger rerender
        setAvatarData({...updatedData});

        // Set highlight flag to provide visual feedback
        setJustSaved(true);
        // Remove highlight after 2 seconds
        setTimeout(() => {
          setJustSaved(false);
        }, 2000);

        // Don't cache responses, always fetch fresh from API
        console.log(`Avatar saved successfully without caching`);

        // Show updated storage state after save
        console.log(`SectionOrchestratorSelector: After saving, updated avatars in storage:`);
        sectionAvatarService.listAllAvatarStorage();
      } else {
        console.warn(`SectionOrchestratorSelector: Failed to save avatar:`, response);
        setNameError(response?.message || 'Failed to update orchestrator');
      }

      // Close dialog on success
      setAvatarDialogOpen(false);
    } catch (err) {
      console.error('Error updating section avatar:', err);

      // Create fallback data for local UI update
      const avatarPhotoUrl = selectedAvatar ? selectedAvatar.photoUrl : avatarData.url;
      const fallbackData = {
        url: avatarPhotoUrl,
        name: customAvatarName,
        sectionId: sectionId
      };

      // Update state with fallback data
      setAvatarData(fallbackData);

      // Don't store in local cache
      console.log('Error saving avatar, but continuing with UI update');

      // Close dialog anyway to prevent getting stuck
      setAvatarDialogOpen(false);

      // Check for specific errors
      if (err.message && err.message.includes('already in use')) {
        setNameError(err.message);
      } else if (err.message.includes('CORS') || err.message.includes('Cross-Origin')) {
        // Special handling for CORS errors
        console.warn('CORS error detected when saving avatar', err);
        setNameError('Browser security restriction prevented saving');

        // Add a helpful alert with more context
        alert(`CORS restriction detected. This is likely a server configuration issue. Your changes have been saved locally but may not persist between sessions. Please contact support.`);
      } else {
        setNameError(err.message || 'Failed to update orchestrator');
        // Show a user-friendly message
        alert("Could not save orchestrator to server. Changes have been applied locally.");
      }
    } finally {
      setSavingAvatar(false);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      mt: 1,
      mb: 2,
      py: 0.5,
      position: 'relative',
      alignItems: 'flex-start'
    }}>
      {/* Orchestrator label */}
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontSize: '0.8rem',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          opacity: 0.9,
          mb: 1
        }}
      >
        Orchestrator:
      </Typography>

      {/* Simple avatar and name layout */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          mt: 0.5,
          ml: 0.5
        }}
      >
        {/* Avatar with edit icon */}
        <Box
          sx={{
            position: 'relative',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.9,
              transform: 'scale(1.05)',
              transition: 'all 0.2s'
            }
          }}
          onClick={handleOpenAvatarDialog}
        >
          {avatarData.url ? (
            <Avatar
              src={avatarData.url}
              alt={avatarData.name || ''}
              onError={() => {
                console.error(`Failed to load avatar image: ${avatarData.url}`);
                setAvatarData({...avatarData, url: null});
              }}
              sx={{
                width: 40,
                height: 40,
                fontSize: '1.2rem',
                background: sectionColorService.getSectionBackgroundColor(sectionId),
                border: justSaved ? '3px solid #4caf50' : 'none', // Green highlight when just saved
                transition: 'all 0.3s ease',
                transform: justSaved ? 'scale(1.05)' : 'scale(1)'
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 40,
                height: 40,
                fontSize: '1.2rem',
                background: sectionColorService.getSectionBackgroundColor(sectionId),
                color: sectionColorService.getSectionIconColor(sectionId),
                border: justSaved ? '3px solid #4caf50' : 'none', // Green highlight when just saved
                transition: 'all 0.3s ease',
                transform: justSaved ? 'scale(1.05)' : 'scale(1)'
              }}
            >
              {avatarData.name ? avatarData.name.charAt(0).toUpperCase() : ''}
            </Avatar>
          )}

          {/* Edit icon - controlled via permission */}
          <PermissionGate permission="foundation.orchestrator.edit">
            <Box
              sx={{
                position: 'absolute',
                right: -6,
                bottom: -6,
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                zIndex: 1,
                border: '1px solid white'
              }}
            >
              <EditIcon sx={{ fontSize: '0.8rem' }} />
            </Box>
          </PermissionGate>
        </Box>

        {/* Show custom name if available, otherwise default name */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            fontSize: '0.9rem',
            color: '#475969'
          }}
        >
          {avatarData.name || ''}
        </Typography>
      </Box>
      
      {/* Avatar Selection Dialog */}
      <Dialog
        open={avatarDialogOpen}
        onClose={handleCloseAvatarDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
        sx: {
          borderRadius: '8px',
          overflow: 'hidden',
          margin: 'auto',
          maxWidth: '70vw',
          width: '70vw',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
        }
      }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          bgcolor: '#f5f8fb'
        }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.125rem', color: '#3a4857' }}>
            Select orchestrator
          </Typography>
          <IconButton onClick={handleCloseAvatarDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{
          pt: 3,
          px: 4,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          boxSizing: 'border-box',
          bgcolor: 'white'
        }}>
          {/* Fixed header section - Name input with current avatar preview */}
          <Box sx={{
            mb: 3,
            py: 2,
            px: 2,
            bgcolor: '#f8f9fa',
            borderRadius: '8px',
            flex: 'none'
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
              {/* Avatar Preview */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {selectedAvatar?.photoUrl || avatarData.url ? (
                  <Box
                    component="img"
                    src={selectedAvatar?.photoUrl || avatarData.url}
                    alt="Selected avatar"
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      border: '3px solid #FFF5C0',
                      objectFit: 'contain',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      fontSize: 24,
                      bgcolor: sectionColorService.getSectionBackgroundColor(sectionId),
                      color: sectionColorService.getSectionIconColor(sectionId),
                      border: '3px solid #FFF5C0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                  >
                    {(customAvatarName || avatarData.name || '').charAt(0).toUpperCase()}
                  </Avatar>
                )}
              </Box>

              {/* Name Input Field */}
              <Box sx={{ flexGrow: 1, ml: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#3a4857' }}>
                  Orchestrator name
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  value={customAvatarName}
                  onChange={(e) => {
                    setCustomAvatarName(e.target.value);
                    if (nameError) setNameError('');
                  }}
                  placeholder="Enter a name for this orchestrator"
                  InputProps={{
                    sx: {
                      borderRadius: '6px',
                      height: '42px',
                      bgcolor: 'white'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4a6da7',
                        borderWidth: '1px'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4a6da7',
                        borderWidth: '1px'
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '15px',
                      '&::placeholder': {
                        fontSize: '14px',
                        opacity: 1,
                        color: 'rgba(0, 0, 0, 0.45)'
                      }
                    }
                  }}
                  error={!!nameError}
                  helperText={nameError || " "}
                />
              </Box>
            </Box>
          </Box>

          {/* Scrollable content section */}
          <Box sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            width: '100%',
            mt: 2
          }}>
            {/* Orchestrator Avatar Selection Title */}
            <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600, color: '#3a4857', flex: 'none' , px: 1}}>
              Select an orchestrator
            </Typography>

            {/* Scrollable Grid Container */}
            <Box sx={{
              flex: 1,
              pt: 1,
              overflowY: 'auto',
              overflowX: 'hidden', // Prevent horizontal scrolling
              pr: 2, // Add extra right padding to avoid scroll appearing on hover
              // Custom scrollbar styling
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>
              {loadingAvatars ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Grid container spacing={2} sx={{ overflow: 'hidden', p: "4px !important" }}>
                  {availableAvatars.map((avatar) => (
                    <Grid item xs={4} sm={3} md={2} key={avatar.id} sx={{ p: "8px !important"}}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: 'none',
                          boxShadow: "rgba(50, 50, 105, 0.15) 0px 2px 5px 0px, rgba(0, 0, 0, 0.05) 0px 1px 1px 0px",
                          borderRadius: '8px',
                          bgcolor: selectedAvatar?.id === avatar.id ? '#FFF5C0' : 'white',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            backgroundColor: 'rgba(255, 245, 192, 0.5)'
                          },
                          height: '100%',
                          overflow: 'hidden',
                          p: 6,
                          mb: "20px !important",
                          mt: "0px !important"
                        }}
                        onClick={() => handleSelectAvatar(avatar)}
                      >
                        <Avatar
                          src={avatar.photoUrl}
                          sx={{
                            width: 52,
                            height: 52,
                            transition: 'all 0.2s ease',
                            boxShadow: selectedAvatar?.id === avatar.id ? '0 0 0 3px rgba(112, 194, 112, 0.2)' : 'none',
                            border: selectedAvatar?.id === avatar.id ? '2px solid #70C270' : 'none'
                          }}
                        />

                        {/* Check icon for selected */}
                        {selectedAvatar?.id === avatar.id && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 6,
                              right: 6,
                              bgcolor: '#70C270',
                              borderRadius: '50%',
                              width: 18,
                              height: 18,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 2,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                          >
                            <CheckIcon
                              sx={{
                                color: 'white',
                                fontSize: 12
                              }}
                            />
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          px: 4,
          py: 2,
          bgcolor: '#fff'
        }}>
          <Button
            onClick={handleCloseAvatarDialog}
            variant="outlined"
            sx={{
              color: '#5a6a7e',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 3,
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.04)"
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAvatar}
            variant="contained"
            sx={{
              bgcolor: '#4a6da7',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              borderRadius: '6px',
              px: 3,
              py: 1,
              '&:hover': {
                bgcolor: '#3d5b8c'
              }
            }}
            disabled={!customAvatarName.trim() || savingAvatar}
            startIcon={savingAvatar && <CircularProgress size={16} color="inherit" />}
          >
            {savingAvatar ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

SectionOrchestratorSelector.propTypes = {
  sectionId: PropTypes.string,
  sectionName: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  allowEdit: PropTypes.bool
};

export default SectionOrchestratorSelector;